import {
  createFigmaNodeConnection,
  deleteFigmaNodeConnection,
  type FigmaNodeConnectionRecord,
} from '@/api/figmaNodeConnections.api';
import { UndoRedoEntry } from '@/components/Recipe/undoRedo';
import { useWorkflowStore } from '@/state/workflow.state';
import type { FigmaNodeData, Node } from '@/types/node';

/**
 * Drop the DB connection row when a connected FigmaNode is removed from the graph, and attach an
 * undo callback that re-inserts it (redo drops again). Does not call Pixie disconnect — the frame
 * must stay so undo can restore local `connectedFigmaFrame`.
 */
export function appendFigmaNodeConnectionPersistence(node: Node, undoEntry: UndoRedoEntry): void {
  const data = node.data as FigmaNodeData;
  const frame = data.connectedFigmaFrame;
  if (!frame) return;

  const workflowId = useWorkflowStore.getState().recipe.id;
  if (!workflowId) return;

  const connection: FigmaNodeConnectionRecord = {
    figmaNodeId: node.id,
    figmaFileKey: frame.fileKey,
    figmaFrameId: frame.nodeId,
  };

  // Best-effort: graph delete must not block on the network. Chain undo/redo mutations on this
  // promise so a late DELETE cannot wipe a just-restored row (and vice versa on redo).
  const dropPromise = deleteFigmaNodeConnection(workflowId, {
    figmaFileKey: connection.figmaFileKey,
    figmaFrameId: connection.figmaFrameId,
  }).catch(() => undefined);

  undoEntry.add(createFigmaNodeConnectionUndoEntry(workflowId, connection, 'create', dropPromise));
}

/**
 * Builds an undo entry that runs `action` when applied, then returns the inverse.
 * Does not run the mutation when called. Mirrors {@link renameUndoEntry}.
 */
function createFigmaNodeConnectionUndoEntry(
  workflowId: string,
  connection: FigmaNodeConnectionRecord,
  action: 'create' | 'delete',
  priorMutation: Promise<unknown>,
): UndoRedoEntry {
  return UndoRedoEntry.fromCallback(() => {
    const mutation = priorMutation
      .catch(() => undefined)
      .then(() =>
        action === 'create'
          ? createFigmaNodeConnection(workflowId, connection)
          : deleteFigmaNodeConnection(workflowId, {
              figmaFileKey: connection.figmaFileKey,
              figmaFrameId: connection.figmaFrameId,
            }),
      )
      .catch(() => undefined);

    return createFigmaNodeConnectionUndoEntry(
      workflowId,
      connection,
      action === 'create' ? 'delete' : 'create',
      mutation,
    );
  });
}
